import { type JobPosting } from '@resolute/schema';
import { Card, Skeleton } from '@resolute/ui';

import { JobCard } from './job-card';

function JobCardSkeleton() {
  return (
    <Card className="flex flex-col gap-4 p-6">
      <div className="flex gap-2">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-5 w-20" />
      </div>
      <Skeleton className="h-6 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-12 w-full" />
      <div className="flex gap-3 pt-2">
        <Skeleton className="h-10 w-28" />
        <Skeleton className="h-10 w-24" />
      </div>
    </Card>
  );
}

export interface JobListProps {
  jobs: JobPosting[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onApply: (jobId: string) => void;
}

export function JobList({ jobs, isLoading, isError, onApply }: JobListProps) {
  if (isLoading) {
    return (
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }, (_, i) => (
          <JobCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <p className="rounded-lg border border-danger/30 bg-danger/10 p-6 text-center text-sm text-danger">
        We couldn&apos;t load open positions right now. Please refresh, or send us a general
        application below.
      </p>
    );
  }

  if (!jobs || jobs.length === 0) {
    return (
      <p className="rounded-lg border border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
        There are no open positions right now — but we&apos;re always happy to hear from great people.
        Send us a general application below.
      </p>
    );
  }

  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {jobs.map((job) => (
        <JobCard key={job.id} job={job} onApply={onApply} />
      ))}
    </div>
  );
}
